import { ErrorBanner } from '../../components/ui/Primitives.js';
import type { useRateSheetRead } from './useRateSheetRead.js';

/**
 * A rate sheet being read, and what was read, for a person to check before it is used: the reading
 * progress, a failure with a retry, and the parsed preview with the columns it came from. The
 * primary button is the caller's ("Use this sheet", "Put into the draft").
 */
export function SheetReadView({ reader, currency, useLabel, pending, error, onUse }: {
  reader: ReturnType<typeof useRateSheetRead>;
  /** The currency the sheet will be used in, to flag a sheet the AI says is in another. */
  currency: string;
  useLabel: string;
  pending: boolean;
  error: unknown;
  onUse: () => void;
}) {
  const { file, parsed, setParsed, ai, blob, send, reset, ok } = reader;
  return (
    <>
      {ai && ai.status === 'reading' && (
        <div className="arc-banner arc-banner-info" role="status" style={{ marginTop: 10 }}>
          <span style={{ flex: 1 }}>Reading {file}… a long PDF takes a minute or two.</span>
          <span className="arc-subtle arc-num" style={{ fontSize: 12 }}>{ai.seconds} s</span>
        </div>
      )}
      {ai && ai.status === 'failed' && (
        <div className="arc-banner arc-banner-danger" role="alert" style={{ marginTop: 10 }}>
          <span style={{ flex: 1 }}>{file} could not be read: {ai.error}</span>
          {blob && <button type="button" className="arc-btn arc-btn-sm" onClick={() => void send(blob)}>Try again</button>}
        </div>
      )}
      {parsed && 'error' in parsed && (
        <div className="arc-banner arc-banner-danger" role="alert" style={{ marginTop: 10 }}>
          <span style={{ flex: 1 }}>{file}: {parsed.error}</span>
          <button type="button" className="arc-btn arc-btn-sm" onClick={() => setParsed(null)}>Dismiss</button>
        </div>
      )}

      {ok && (
        <div className="arc-stack" style={{ gap: 8, marginTop: 10, padding: 12, border: '1px solid var(--arc-border)', borderRadius: 'var(--arc-r-sm)' }}>
          <div style={{ fontSize: 13 }}>
            <strong>{ok.rules.length.toLocaleString()}</strong> rates read from <span className="arc-mono">{file}</span>
            {ok.skipped > 0 && <>, <span style={{ color: 'var(--arc-warn)' }}>{ok.skipped} rows skipped</span></>}.
            <span className="arc-subtle"> Prefix from “{ok.columns.prefix}”, rate from “{ok.columns.rate}”
              {ok.columns.increments ? `, increments from “${ok.columns.increments}”` : ', per second'}.</span>
          </div>
          {ai?.how === 'ai' && (
            <div className="arc-banner arc-banner-warn" role="note">
              <span style={{ flex: 1 }}>
                Read by AI. Check a few rates against the sheet before using it.
                {ai.currency && ai.currency !== currency ? ` The sheet is in ${ai.currency}; this is a ${currency} sheet.` : ''}
              </span>
            </div>
          )}
          {ai && ai.notes.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
              {ai.notes.map((n) => <li key={n}>{n}</li>)}
            </ul>
          )}
          {ok.problems.length > 0 && (
            <ul className="arc-subtle" style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
              {ok.problems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          )}
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead><tr><th>Prefix</th><th>Destination</th><th className="arc-td-right">A minute</th><th className="arc-td-right">Increments</th></tr></thead>
              <tbody>
                {ok.rules.slice(0, 6).map((r) => (
                  <tr key={r.prefix}>
                    <td className="arc-mono">{r.prefix}</td><td>{r.description ?? '—'}</td>
                    <td className="arc-td-right arc-num">{r.rate_per_minute}</td>
                    <td className="arc-td-right arc-num">{r.first_increment_sec}/{r.subsequent_increment_sec} s</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {error ? <ErrorBanner error={error} /> : null}
          <div className="arc-row" style={{ gap: 8 }}>
            <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={ok.rules.length === 0 || pending}
              onClick={onUse}>
              {pending ? 'Saving…' : useLabel}
            </button>
            <button type="button" className="arc-btn arc-btn-sm" onClick={reset}>Cancel</button>
            {ai?.how !== 'ai' && blob && (
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => void send(blob, true)}
                title="If the columns above were read wrongly">
                Read it with AI instead
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
